import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { basisPeriod, validateSetup } from "../domain/preparation";
import { formatAmount } from "../../../../utils/hooks/dataHandler";

const disclaimers = [
  "Companies with income or loss items taxed at different or concessionary tax rates, including Section 37A adjustments. This feature assumes the normal 17% tax rate.",
  "Incentive companies or companies with a mixture of exempt, concessionary-rate, and normal-rate activities.",
  "Newly incorporated companies whose first accounts cover more than 12 months. Income and losses must first be apportioned between the relevant YAs.",
  "Companies with complex share structures involving preference shares, non-commercial loans, unusual profit rights, or winding-up rights.",
  "Companies with different qualifying periods for different company relationships. This feature stores a simplified qualifying period for each company rather than testing the complete ownership structure for every pair.",
  "Acquisitions, restructurings, or arrangements involving anticipated losses. IRAS may deny excessive claims under the anti-avoidance rule.",
  "Situations involving incorrect SUTE classification. This tool accepts the user's SUTE selection without testing all statutory conditions.",
  "Situations involving other reliefs, credits, or computations not entered into this feature, such as foreign tax credits or special industry rules.",
];

export default function CaseSetupStep({ settings, updateSettings, attempted }) {
  const [taxSettingsOpen, setTaxSettingsOpen] = useState(true);
  const errors = validateSetup(settings);
  const basis = basisPeriod(settings.commonFinancialYearEnd);
  const field = (key, label, type = "number") => (
    <TextField
      fullWidth
      label={label}
      type={type}
      value={settings[key]}
      onChange={(event) => updateSettings({ [key]: event.target.value })}
      error={attempted && Boolean(errors[key])}
      helperText={
        (attempted && errors[key]) ||
        {
          yearOfAssessment: "Used to find matching saved Tax Computations.",
          commonFinancialYearEnd:
            "Shared by all participating companies; must fall in the year before the YA.",
          rebatePercentage: "Percentage used for this case's tax comparison.",
          rebateCap: "Maximum rebate per company, in SGD.",
        }[key]
      }
      InputLabelProps={{ shrink: true }}
      inputProps={
        type === "number"
          ? { min: 0, step: key === "rebatePercentage" ? "any" : 1 }
          : {}
      }
    />
  );
  return (
    <Box sx={{ maxWidth: 960, mx: "auto" }}>
      <Typography variant='h5' fontWeight={750} gutterBottom>
        Set up your case
      </Typography>
      <Box sx={{ mb: 3 }}>
        <Typography
          color='text.secondary'
          sx={{
            fontSize: "0.95rem",
            lineHeight: 1.6,
            mb: 0.5,
          }}
        >
          These settings apply to the entire case.
        </Typography>

        <Typography
          sx={{
            fontSize: "0.95rem",
            lineHeight: 1.6,
            fontWeight: 500,
            color: "text.primary",
          }}
        >
          Select the YA and Financial Year End to find matching saved Tax
          Computations.
        </Typography>
      </Box>
      <Paper variant='outlined' sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ mb: 3 }}
        >
          <Typography fontWeight={700}>Case details</Typography>
        </Stack>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            {field("yearOfAssessment", "Year of Assessment (YA)")}
          </Grid>
          <Grid item xs={12} sm={6}>
            {field(
              "commonFinancialYearEnd",
              "Common financial year end",
              "date"
            )}
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, p: 2, bgcolor: "action.hover", borderRadius: 2 }}>
          <Typography variant='overline' color='text.secondary'>
            Basis period
          </Typography>
          <Typography fontWeight={650}>
            {basis
              ? `${basis.start} to ${basis.end}`
              : "Choose a valid financial year end"}
          </Typography>
          {basis && (
            <Box>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ lineHeight: 1.6 }}
              >
                {basis.days} calendar days · 12-month period ending on the
                common financial year end.
              </Typography>

              <Typography
                variant='body2'
                sx={{
                  mt: 0.5,
                  color: "text.primary",
                  fontWeight: 500,
                  lineHeight: 1.6,
                }}
              >
                Please verify each company's accounting and qualifying periods
                before ticking the figures check.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
      <Accordion
        expanded={
          taxSettingsOpen ||
          Boolean(attempted && (errors.rebatePercentage || errors.rebateCap))
        }
        onChange={(_, expanded) => setTaxSettingsOpen(expanded)}
        disableGutters
        elevation={0}
        sx={{
          mt: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px !important",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography fontWeight={700}>Tax settings</Typography>
            <Typography variant='body2' color='text.secondary'>
              CIT rebate {settings.rebatePercentage || "0"}% · Cap SGD{" "}
              {formatAmount(settings.rebateCap || "0")}
            </Typography>
            {attempted && (errors.rebatePercentage || errors.rebateCap) && (
              <Typography variant='body2' color='error'>
                Check the rebate settings.
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              {field("rebatePercentage", "CIT rebate (%)")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("rebateCap", "CIT rebate cap (SGD)")}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Box sx={{ mt: 2, color: "text.secondary" }}>
        <Typography
          variant='caption'
          component='p'
          sx={{
            lineHeight: 1.6,
            m: 0,
          }}
        >
          You can review your case settings at any time from{" "}
          <strong>Case & Guide</strong> beside the page title. If you change
          these settings, please review the affected companies and tick their
          figures checks again.
        </Typography>

        <Box
          sx={{
            mt: 1.5,
            pt: 1.25,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant='caption'
            component='p'
            fontWeight={600}
            sx={{
              mb: 0.5,
              color: "text.secondary",
            }}
          >
            Disclaimer
          </Typography>

          <Typography
            variant='caption'
            component='p'
            sx={{
              m: 0,
              lineHeight: 1.6,
            }}
          >
            The group relief optimiser tool is not intended to cover the
            following scenarios:
          </Typography>

          <Box
            component='ul'
            sx={{
              mt: 0.5,
              mb: 0,
              pl: 2.25,
              "& > li + li": {
                mt: 0.2,
              },
              "& > li::marker": {
                color: "text.disabled",
              },
            }}
          >
            {disclaimers.map((disclaimer) => (
              <Typography
                key={disclaimer}
                component='li'
                variant='caption'
                sx={{
                  lineHeight: 1.55,
                  color: "text.secondary",
                }}
              >
                {disclaimer}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
